/**
 * useToast — imperative toast trigger backed by a tiny external store.
 *
 * Components call `push({ title, body, variant })` from event handlers; the
 * `<Toaster />` primitive (rendered once at the AppShell root) subscribes and
 * animates the stack. Auto-dismiss is variant-aware (4s default, 7s for
 * danger, 0 = manual for critical).
 *
 * Pattern intentionally avoids context so any component can dispatch a toast
 * without needing a provider above it.
 */

import { useSyncExternalStore } from "react";

export type ToastVariant = "info" | "success" | "warning" | "danger";

export interface ToastInput {
  title: string;
  body?: string;
  variant?: ToastVariant;
  /** Override auto-dismiss in ms. 0 = manual (user dismisses). */
  duration?: number;
  /** An in-app link shown under the body. */
  link?: { label: string; to: string };
}

export interface ToastItem extends Required<Omit<ToastInput, "body" | "link">> {
  id: string;
  body?: string;
  link?: { label: string; to: string };
}

type Listener = () => void;

const DEFAULT_DURATION: Record<ToastVariant, number> = {
  info: 4000,
  success: 4000,
  warning: 5000,
  danger: 7000,
};

/* Replaced, never mutated: `useSyncExternalStore` only re-renders when the
   snapshot is a new array. */
let items: readonly ToastItem[] = [];
const listeners = new Set<Listener>();

function notify(): void {
  for (const l of listeners) l();
}

function subscribe(l: Listener): () => void {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
}

function getSnapshot(): readonly ToastItem[] {
  return items;
}

function makeId(): string {
  return `t_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`;
}

export function pushToast(input: ToastInput): string {
  const variant: ToastVariant = input.variant ?? "info";
  const duration = input.duration ?? DEFAULT_DURATION[variant];
  const item: ToastItem = {
    id: makeId(),
    title: input.title,
    body: input.body,
    link: input.link,
    variant,
    duration,
  };
  items = [...items, item];
  notify();
  if (duration > 0) {
    setTimeout(() => dismissToast(item.id), duration);
  }
  return item.id;
}

export function dismissToast(id: string): void {
  if (!items.some((it) => it.id === id)) return;
  items = items.filter((it) => it.id !== id);
  notify();
}

export function useToast(): {
  toasts: readonly ToastItem[];
  push: (input: ToastInput) => string;
  dismiss: (id: string) => void;
} {
  const toasts = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  return { toasts, push: pushToast, dismiss: dismissToast };
}
